import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Divider,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  AccessTime as TimeIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  FitnessCenter as FitnessIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isToday, isTomorrow, isThisWeek } from 'date-fns';
import { enUS as en, tr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useUserPlan } from '../hooks/useUserPlan';
import ClassBookingDialog from '../components/ClassBookingDialog';
import api, { handleApiError } from '../utils/api';

const ViewScheduledClassesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { userPlan } = useUserPlan();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Booking dialog states
  const [bookingDialog, setBookingDialog] = useState({
    open: false,
    classData: null
  });

  // Filter states
  const [filters, setFilters] = useState({
    date: null,
    category: '',
    trainer: '',
    facility: '',
    status: 'scheduled',
    upcoming: true
  });

  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    trainers: [],
    facilities: []
  });

  useEffect(() => {
    loadClasses();
    loadFilterOptions();
  }, []);

  useEffect(() => {
    loadClasses();
  }, [filters]);

  const loadClasses = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      
      if (filters.date) {
        // Format date without timezone shift
        const selectedDate = new Date(filters.date);
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');

        // Create start and end of day in local time, then convert to ISO
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        params.append('startDate', startOfDay.toISOString());
        params.append('endDate', endOfDay.toISOString());
      } else if (filters.upcoming) {
        params.append('upcoming', 'true');
      }
      
      if (filters.category) params.append('category', filters.category);
      if (filters.trainer) params.append('trainer', filters.trainer);
      if (filters.facility) params.append('facility', filters.facility);
      if (filters.status) params.append('status', filters.status);
      
      params.append('limit', '50'); // Get more classes for better browsing

      const response = await api.get(`/scheduled-classes?${params.toString()}`);
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Error loading classes:', error);
      setError(t('classes:messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [trainersRes, facilitiesRes, classTypesRes] = await Promise.all([
        api.get('/trainer-profiles'),
        api.get('/facilities'),
        api.get('/class-types')
      ]);

      const categories = [...new Set(
        (classTypesRes.data.data || []).map(ct => ct.category).filter(Boolean)
      )];

      setFilterOptions({
        categories,
        trainers: trainersRes.data.data || [],
        facilities: facilitiesRes.data.data || []
      });
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      date: null,
      category: '',
      trainer: '',
      facility: '',
      status: 'scheduled',
      upcoming: true
    });
  };

  const handleViewDetails = (classId) => {
    navigate(`/classes/${classId}`);
  };

  const handleQuickBook = (classData) => {
    setBookingDialog({
      open: true,
      classData
    });
  };

  const handleBookingDialogClose = () => {
    setBookingDialog({
      open: false,
      classData: null
    });
  };

  const handleBookingSuccess = () => {
    setSuccess(t('classes:messages.bookedSuccess'));
    loadClasses(); // Refresh to show updated enrollment
    handleBookingDialogClose();
  };

  const isUserBookedForClass = (scheduledClass) => {
    if (!scheduledClass || !user) return false;
    return scheduledClass.attendees?.some(attendee => 
      (attendee.user._id === user._id || attendee.user === user._id) &&
      (attendee.status === 'booked' || attendee.status === 'attended' || attendee.status === 'pending_payment')
    );
  };

  const handleCancelBooking = async (classId) => {
    try {
      setLoading(true);
      await api.delete(`/scheduled-classes/${classId}/unbook`);
      setSuccess(t('classes:messages.cancelledSuccess'));
      await loadClasses(); // Refresh to show updated enrollment
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'success';
      case 'full': return 'warning';
      case 'in-progress': return 'info';
      case 'completed': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getDateLabel = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) return t('classes:date.today');
    if (isTomorrow(date)) return t('classes:date.tomorrow');
    if (isThisWeek(date)) return format(date, 'EEEE', { locale: i18n.language === 'tr' ? tr : en });
    return format(date, 'MMM dd', { locale: i18n.language === 'tr' ? tr : en });
  };

  const formatTime = (dateString) => {
    return format(new Date(dateString), 'HH:mm');
  };

  const formatDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMinutes = Math.round((end - start) / (1000 * 60));
    return t('classes:time.duration', { minutes: durationMinutes });
  };

  const getAvailabilityInfo = (scheduledClass) => {
    // Default to overall class capacity if no plan access configured
    let enrolled = scheduledClass.currentEnrollment || 0;
    let capacity = scheduledClass.capacity;
    let available = capacity - enrolled;
    let planName = 'General';

    // If class has plan access and user has a plan, show plan-specific availability
    if (scheduledClass.planAccess && scheduledClass.planAccess.length > 0 && userPlan) {
      const userPlanAccess = scheduledClass.planAccess.find(pa => 
        pa.plan._id === userPlan._id
      );
      
      if (userPlanAccess) {
        enrolled = userPlanAccess.currentParticipants || 0;
        capacity = userPlanAccess.maxParticipants;
        available = capacity - enrolled;
        planName = userPlan.name;
      } else {
        // User's plan doesn't have access - shouldn't happen if filtering works correctly
        available = 0;
        capacity = 0;
        enrolled = 0;
        planName = 'No Access';
      }
    }
    
    return {
      enrolled,
      capacity,
      available,
      isFull: available <= 0,
      isAlmostFull: available <= Math.ceil(capacity * 0.2) && available > 0,
      planName
    };
  };

  if (loading && classes.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            {t('classes:title')}
          </Typography>
          <Tooltip title={t('classes:refresh')}>
            <IconButton onClick={loadClasses} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('classes:filters.title')}
          </Typography>
          <Grid container spacing={2} alignItems="center" sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <DatePicker
                label={t('classes:filters.date')}
                value={filters.date}
                onChange={(value) => handleFilterChange('date', value)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="classes-category-label">{t('classes:filters.category')}</InputLabel>
                <Select
                  id="classes-category-select"
                  labelId="classes-category-label"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  label={t('classes:filters.category')}
                >
                  <MenuItem value="">{t('classes:filters.allCategories')}</MenuItem>
                  {filterOptions.categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="classes-trainer-label">{t('classes:filters.trainer')}</InputLabel>
                <Select
                  id="classes-trainer-select"
                  labelId="classes-trainer-label"
                  value={filters.trainer}
                  onChange={(e) => handleFilterChange('trainer', e.target.value)}
                  label={t('classes:filters.trainer')}
                >
                  <MenuItem value="">{t('classes:filters.allTrainers')}</MenuItem>
                  {filterOptions.trainers.map((trainer) => (
                    <MenuItem key={trainer._id} value={trainer.user?._id || trainer._id}>
                      {trainer.user?.firstName || trainer.firstName} {trainer.user?.lastName || trainer.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="classes-facility-label">{t('classes:filters.facility')}</InputLabel>
                <Select
                  id="classes-facility-select"
                  labelId="classes-facility-label"
                  value={filters.facility}
                  onChange={(e) => handleFilterChange('facility', e.target.value)}
                  label={t('classes:filters.facility')}
                >
                  <MenuItem value="">{t('classes:filters.allFacilities')}</MenuItem>
                  {filterOptions.facilities.map((facility) => (
                    <MenuItem key={facility._id} value={facility._id}>
                      {facility.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="classes-status-label">{t('classes:filters.status')}</InputLabel>
                <Select
                  id="classes-status-select"
                  labelId="classes-status-label"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label={t('classes:filters.status')}
                >
                  <MenuItem value="">{t('classes:filters.allStatuses')}</MenuItem>
                  <MenuItem value="scheduled">{t('classes:filters.available')}</MenuItem>
                  <MenuItem value="full">{t('classes:filters.full')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button variant="outlined" onClick={clearFilters} fullWidth>
                {t('classes:filters.clearFilters')}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Classes Grid */}
        {classes.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <FitnessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('classes:empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('classes:empty.subtitle')}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3} sx={{ width: '100%' }}>
            {classes.map((scheduledClass) => {
              const availability = getAvailabilityInfo(scheduledClass);
              
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={scheduledClass._id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Class Type and Status */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                          {scheduledClass.classType?.name}
                        </Typography>
                        <Chip
                          label={scheduledClass.status}
                          color={getStatusColor(scheduledClass.status)}
                          size="small"
                        />
                      </Box>

                      {/* Date and Time */}
                      <Box display="flex" alignItems="center" mb={1}>
                        <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {getDateLabel(scheduledClass.startTime)}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" mb={1}>
                        <TimeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(scheduledClass.startTime)} - {formatTime(scheduledClass.endTime)}
                          <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                            ({formatDuration(scheduledClass.startTime, scheduledClass.endTime)})
                          </Typography>
                        </Typography>
                      </Box>

                      {/* Trainer */}
                      <Box display="flex" alignItems="center" mb={1}>
                        <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {scheduledClass.trainer?.firstName} {scheduledClass.trainer?.lastName}
                        </Typography>
                      </Box>

                      {/* Facility */}
                      <Box display="flex" alignItems="center" mb={2}>
                        <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {scheduledClass.facility?.name}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Availability */}
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                          <GroupIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {t('classes:class.enrolled', { enrolled: availability.enrolled, capacity: availability.capacity })}
                            {availability.planName !== 'General' && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic' }}>
                                ({availability.planName})
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                        {availability.isFull ? (
                          <Chip
                            label={availability.planName === 'No Access' ? t('classes:class.status.notAvailable') : t('classes:class.status.full')}
                            color="warning"
                            size="small"
                          />
                        ) : availability.isAlmostFull ? (
                          <Chip label={t('classes:class.spotsLeft', { count: availability.available })} color="info" size="small" />
                        ) : (
                          <Chip label={t('classes:class.status.available')} color="success" size="small" />
                        )}
                      </Box>

                      {/* Class Description */}
                      {scheduledClass.classType?.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          {scheduledClass.classType.description.length > 100
                            ? `${scheduledClass.classType.description.substring(0, 100)}...`
                            : scheduledClass.classType.description
                          }
                        </Typography>
                      )}
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        size="small"
                        onClick={() => handleViewDetails(scheduledClass._id)}
                        sx={{ mr: 1 }}
                      >
                        {t('classes:buttons.viewDetails')}
                      </Button>
                      {scheduledClass.status === 'scheduled' && availability.planName !== 'No Access' && (
                        <>
                          {isUserBookedForClass(scheduledClass) ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleCancelBooking(scheduledClass._id)}
                              disabled={loading}
                            >
                              {t('classes:buttons.cancelBooking')}
                            </Button>
                          ) : !availability.isFull ? (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleQuickBook(scheduledClass)}
                              disabled={loading}
                            >
                              {t('classes:buttons.quickBook')}
                            </Button>
                          ) : null}
                        </>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* Booking Dialog */}
      <ClassBookingDialog
        open={bookingDialog.open}
        onClose={handleBookingDialogClose}
        classData={bookingDialog.classData}
        onSuccess={handleBookingSuccess}
      />
    </LocalizationProvider>
  );
};

export default ViewScheduledClassesPage;
